import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTheme } from '@withjoy/joykit/theme';
import { DropletIcon, WindIcon, UvIcon, PrecipIcon } from './WeatherIcons';
import { useTranslation } from '@shared/core/i18n';
import { clamp01, getUvCategory } from '../WeatherReport.helpers';

type MeterProps = {
  value01: number; // normalized 0..1
  gradient?: string;
  color?: string;
};

export type TemperatureGradient = string | ((range: { min: number; max: number }) => string);

export type MetricsGradients = {
  precip: string;
  humidity: string;
  wind: string;
  uv: string;
  temperature: TemperatureGradient; // used for TemperatureRangeBar
};

const MeterTrack = ({ value01, gradient, color }: MeterProps) => {
  const { colors } = useTheme();
  const leftPct = `${clamp01(value01) * 100}%`;
  return (
    <Box position="relative">
      <Box
        height="6px"
        borderRadius={999}
        background={gradient || color || colors.mono4}
        boxShadow="inset 0 0 0 1px rgba(0,0,0,0.04)"
        width="100%"
      />
      <Box
        position="absolute"
        top="50%"
        left={leftPct}
        transform="translate(-50%, -50%)"
        width="10px"
        height="10px"
        borderRadius={999}
        backgroundColor="white"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="mono4"
        boxShadow="0 1px 1px rgba(0,0,0,0.08)"
      />
    </Box>
  );
};

// ----- Temperature range (for daily forecast) -----
export const TemperatureRangeBar = ({
  min,
  max,
  low,
  high,
  height = 8,
  gradient,
}: {
  min: number;
  max: number;
  low: number;
  high: number;
  height?: number | string;
  gradient?: string;
}) => {
  const span = Math.max(0.0001, max - min);
  const clampedLow = Math.max(min, Math.min(max, low));
  const clampedHigh = Math.max(min, Math.min(max, Math.max(low, high)));
  const leftPct = ((clampedLow - min) / span) * 100;
  const widthPct = ((clampedHigh - clampedLow) / span) * 100;

  // Use provided gradient (accent-aware) to keep visuals aligned to host palette.
  const FULL_GRADIENT = gradient || 'linear-gradient(90deg,#d4d4d4 0%, #9ca3af 100%)';
  const rightClip = 100 - leftPct - widthPct;

  return (
    <Box
      position="relative"
      width="100%"
    >
      <Box
        height={typeof height === 'number' ? `${height}px` : height}
        borderRadius={999}
        backgroundColor="mono4"
        width="100%"
      />
      {/* Full-width gradient clipped to the day's segment */}
      <Box
        position="absolute"
        top="50%"
        left={0}
        transform="translateY(-50%)"
        height={typeof height === 'number' ? `${height}px` : height}
        width="100%"
        borderRadius={999}
        background={FULL_GRADIENT}
        __css={{ clipPath: `inset(0% ${rightClip}% 0% ${leftPct}% round 999px)` }}
        boxShadow="inset 0 0 0 1px rgba(0,0,0,0.04)"
      />
    </Box>
  );
};

// ----- UV -----
export const UvMeter = ({ uv, gradient, textColor = 'mono12', iconColor = 'mono14' }: { uv?: number; gradient: string; textColor?: string; iconColor?: string }) => {
  const { t } = useTranslation('websiteEditor');
  const trans = t('customPage', 'weatherReport', 'metrics');
  if (typeof uv !== 'number') return null;
  const cat = getUvCategory(uv);
  const pos01 = clamp01(uv / 11);
  return (
    <Box>
      <Flex
        alignItems="center"
        gap={2}
        marginBottom={1}
      >
        <Box color={iconColor}>
          <UvIcon />
        </Box>
        <TextV2
          typographyVariant="label3"
          color={textColor}
        >
          {trans.uvLabel({ uv, category: trans.uvCategory[cat.key]() })}
        </TextV2>
      </Flex>
      <MeterTrack
        value01={pos01}
        gradient={gradient}
      />
    </Box>
  );
};

// ----- Percent meters (precip/humidity) -----
export const PercentMeter = ({
  valuePct,
  label,
  icon,
  gradient,
  textColor = 'mono12',
  iconColor = 'mono14',
}: {
  valuePct?: number;
  label: string;
  icon: React.ReactNode;
  gradient: string;
  textColor?: string;
  iconColor?: string;
}) => {
  if (typeof valuePct !== 'number') return null;
  const v01 = clamp01(valuePct / 100);
  return (
    <Box>
      <Flex
        alignItems="center"
        gap={2}
        marginBottom={1}
      >
        <Box color={iconColor}>{icon}</Box>
        <TextV2
          typographyVariant="label3"
          color={textColor}
        >
          {label}: {Math.round(valuePct)}%
        </TextV2>
      </Flex>
      <MeterTrack
        value01={v01}
        gradient={gradient}
      />
    </Box>
  );
};

// ----- Wind -----
export const WindMeter = ({ kph, gradient, textColor = 'mono12', iconColor = 'mono14' }: { kph?: number; gradient: string; textColor?: string; iconColor?: string }) => {
  const { t } = useTranslation('websiteEditor');
  const trans = t('customPage', 'weatherReport', 'metrics');
  if (typeof kph !== 'number') return null;
  const cap = 60; // normalize up to ~breezy/strong
  const v01 = clamp01(kph / cap);
  const labelKey = kph < 10 ? 'calm' : kph < 25 ? 'breezy' : kph < 40 ? 'windy' : 'strong';
  return (
    <Box>
      <Flex
        alignItems="center"
        gap={2}
        marginBottom={1}
      >
        <Box color={iconColor}>
          <WindIcon />
        </Box>
        <TextV2
          typographyVariant="label3"
          color={textColor}
        >
          {trans.windLabel({ kph: Math.round(kph), label: trans.windCategory[labelKey]() })}
        </TextV2>
      </Flex>
      <MeterTrack
        value01={v01}
        gradient={gradient}
      />
    </Box>
  );
};

// ----- Pill wrapper -----
export const MetricPill = ({ children, bg = 'mono1', border = 'mono3' }: { children: React.ReactNode; bg?: string; border?: string }) => (
  <Box
    paddingY={2}
    paddingX={3}
    borderRadius={12}
    backgroundColor={bg}
    borderWidth="1px"
    borderStyle="solid"
    borderColor={border}
    minWidth={['100%', null, '220px']}
  >
    {children}
  </Box>
);

// Convenience metric blocks
export const PrecipMetric = ({
  precip01,
  gradients,
  pillBg,
  pillBorder,
  textColor,
  iconColor,
}: {
  precip01?: number;
  gradients: MetricsGradients;
  pillBg: string;
  pillBorder: string;
  textColor?: string;
  iconColor?: string;
}) => {
  const { t } = useTranslation('websiteEditor');
  const labels = t('customPage', 'weatherReport', 'metrics', 'labels');
  return (
    <MetricPill
      bg={pillBg}
      border={pillBorder}
    >
      <PercentMeter
        valuePct={typeof precip01 === 'number' ? precip01 * 100 : undefined}
        icon={<PrecipIcon />}
        label={labels.precip()}
        gradient={gradients.precip}
        textColor={textColor}
        iconColor={iconColor}
      />
    </MetricPill>
  );
};

export const HumidityMetric = ({
  pct,
  gradients,
  pillBg,
  pillBorder,
  textColor,
  iconColor,
}: {
  pct?: number;
  gradients: MetricsGradients;
  pillBg: string;
  pillBorder: string;
  textColor?: string;
  iconColor?: string;
}) => {
  const { t } = useTranslation('websiteEditor');
  const labels = t('customPage', 'weatherReport', 'metrics', 'labels');
  return (
    <MetricPill
      bg={pillBg}
      border={pillBorder}
    >
      <PercentMeter
        valuePct={pct}
        icon={<DropletIcon />}
        label={labels.humidity()}
        gradient={gradients.humidity}
        textColor={textColor}
        iconColor={iconColor}
      />
    </MetricPill>
  );
};

export const WindSpeedMetric = ({
  kph,
  gradients,
  pillBg,
  pillBorder,
  textColor,
  iconColor,
}: {
  kph?: number;
  gradients: MetricsGradients;
  pillBg: string;
  pillBorder: string;
  textColor?: string;
  iconColor?: string;
}) => (
  <MetricPill
    bg={pillBg}
    border={pillBorder}
  >
    <WindMeter
      kph={kph}
      gradient={gradients.wind}
      textColor={textColor}
      iconColor={iconColor}
    />
  </MetricPill>
);

export const UvIndexMetric = ({
  uv,
  gradients,
  pillBg,
  pillBorder,
  textColor,
  iconColor,
}: {
  uv?: number;
  gradients: MetricsGradients;
  pillBg: string;
  pillBorder: string;
  textColor?: string;
  iconColor?: string;
}) => (
  <MetricPill
    bg={pillBg}
    border={pillBorder}
  >
    <UvMeter
      uv={uv}
      gradient={gradients.uv}
      textColor={textColor}
      iconColor={iconColor}
    />
  </MetricPill>
);
